import { ApiTemplateCustomization, EnumValueType } from '@Model';
import { type Undefined, useStateFromProps } from '@synqo/frontend-component-library';
import { useCallback } from 'react';
import { type CustomizationUpdater } from './props';

/**
 * Updates the value of the given `customization`. Based on the type, either the `source`
 * or the `value` property are set.
 * @param customization The customization to update
 * @param onUpdate The callback to trigger when the value of the customization is changed.
 * @returns
 */
export function useUpdateCustomization(
    customization: Undefined<ApiTemplateCustomization>,
    onUpdate: CustomizationUpdater,
) {
    const isHtml = customization?.type === EnumValueType.Html;
    const initialState = isHtml ? customization?.source : customization?.value;

    const [state, setState] = useStateFromProps(initialState ?? '');

    const updater = useCallback(
        (value: string) => {
            if (!customization) {
                return false;
            }

            setState(value);

            if (value === initialState) {
                return true;
            }

            if (isHtml) {
                onUpdate({ id: customization.id, source: value, value: '' });
            } else {
                onUpdate({ id: customization.id, source: '', value: value });
            }

            return true;
        },
        [customization, setState, initialState, isHtml, onUpdate],
    );

    return {
        state,
        setState: updater,
    };
}
